<template>
  <vod-item-link
    ref="vodItemLink"
    :to="to"
    :item="item"
    :class="['vod-item', {'black-theme': !themeWhite}]"
    data-wptv="vod-item"
    @select-item="onSelectItem()">
    <notification :show="hasNotification" />
    <div
      v-if="alertPublished"
      class="item-alert-tag">
      {{ $t('myalerts.status.published') }}
    </div>
    <wptv-poster
      :show-gradient="false"
      :show-logo="false"
      :show-subscribe="false"
      :show-csa="false"
      :show-progress="showProgressBar"
      :progress-value="progressValue"
      :progress-disabled="progressDisabled"
      :image-data="imageData"
      :alt-image="item.title"
      :actions="actions"
      :ratio="imageData.ratio">
      <div slot="thumbnails">
        <wptv-thumbnail
          v-if="remainingRentTime"
          slot="remainingTime"
          :text="remainingRentTime"
          data-wptv="vignette-temps-restant"
          mode="time" />
        <wptv-thumbnail
          v-if="pastilInfo.type === 'promotion'"
          slot="poster"
          :text="pastilInfo.text"
          :background-color="pastilInfo.color"
          mode="state" />
      </div>
    </wptv-poster>

    <div
      v-if="showTitle"
      class="item-title-wrapper">
      <span
        class="item-title"
        data-wptv="titre-item">
        {{ item.title }}
      </span>
      <span
        v-if="item.csa && item.csa > 1"
        class="item-csa"
        data-wptv="csa-item">
        <wptv-icon
          :name="'csa-round' + item.csa"
          :invert="!themeWhite"
          :specific-alt="$t(`general.csa.${item.csa}`)"
          width="1.125rem"
          height="1.125rem" />
      </span>
    </div>
    <div
      v-if="showSeasonSumup"
      class="item-season-sumup"
      data-wptv="resume-saison">
      {{ item.seasonsSumup }}
    </div>
  </vod-item-link>
</template>

<script>
import _get from 'lodash/get'
import { mapGetters } from 'vuex'
import { setItemImageData } from '../imageData.utils'
import { WptvIcon } from '@wptv/components/ui/icon'
import { WptvPoster, WptvPosterConfig } from '@wptv/components/ui/poster'
import { WptvThumbnail } from '@wptv/components/ui/thumbnail'
import API_CONSTANTS from '../../../api.constants'
import FipUtils from '../../fip/shared/fip.utils'
import Notification from '../notification/notification'
import RouteUtils from '~/route.utils'
import VodItemLink from './vodItemLink/VodItemLink'

export default {
  name: 'VodItem',
  components: {
    Notification,
    WptvPoster,
    WptvThumbnail,
    WptvIcon,
    VodItemLink
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    favoriteFormat: {
      type: String,
      default: 'COVER'
    },
    availableFormats: {
      type: Array,
      default: () => ['COVER']
    },
    themeWhite: {
      type: Boolean,
      default: true
    },
    showSeasonSumup: {
      type: Boolean,
      default: false
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    promo: {
      type: Object,
      default: () => ({})
    },
    displayTrash: {
      type: String,
      default: null
    },
    remainingRentTime: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      imageData: setItemImageData(this.item, { favoriteFormat: this.favoriteFormat, availableFormats: this.availableFormats }, this.$config),
      showProgressBar: false,
      progressValue: 0,
      progressDisabled: false,
      timecode: 0
    }
  },
  computed: {
    ...mapGetters({
      playHistoryAll: 'user/getPlayHistory',
      findPlayHistoryById: 'user/findPlayHistoryById'
    }),
    actions () {
      const removeAction = {
        name: WptvPosterConfig.ACTIONS.REMOVE,
        label: this.displayTrash,
        dataWptv: 'mosaique-supprimer',
        onClick: event => this.deleteBookmark(this.item, event)
      }

      return this.displayTrash ? [removeAction] : []
    },
    to () {
      return {
        name: RouteUtils.getPageRouteName(this.item.type, this.item.videoType),
        params: RouteUtils.getPageRouteParams(this.item, this.promo),
        link: this.item.link || ''
      }
    },
    hasNotification () {
      return this.item.isAlert && _get(this.item, 'notification', '') === API_CONSTANTS.NOTIFICATIONS.NOTIFIED
    },
    alertPublished () {
      return this.item.isAlert && _get(this.item, 'status', '') === API_CONSTANTS.ALERT_STATUS.PUBLISHED
    },
    pastilInfo () {
      return _get(this.promo, 'pastilInfo', {})
    },
    duration () {
      return _get(this.item, 'duration', 1) * 60000
    },
    locationExpired () {
      return FipUtils.isLocationExpired(this.$store, this.item.id)
    }
  },
  watch: {
    playHistoryAll () {
      this.setProgressBar()
    }
  },
  mounted () {
    if (this.playHistoryAll?.length > 0) {
      this.setProgressBar()
    }
  },
  methods: {
    deleteBookmark (item, event) {
      event.stopPropagation() // to avoid triggering other vodItem event
      this.$emit('delete-bookmark', item.id)
    },
    onSelectItem () {
      this.$emit('select-item', this.item)
    },
    setProgressBar () {
      // FDE: [6019] reduce heavy category loading time by doing this after component display
      const playHistory = this.findPlayHistoryById(this.item?.id)
      this.timecode = playHistory?.timecode || 0
      this.showProgressBar = this.item.type === API_CONSTANTS.RESOURCE_TYPES.VIDEO && !!playHistory
      this.progressValue = this.timecode * 100 / this.duration
      this.progressDisabled = !playHistory || this.locationExpired
    }
  }
}
</script>

<style lang="scss" scoped>
.vod-item {
  position: relative;

  &.black-theme,
  &.black-theme::v-deep a {
    color: $white;

    &:hover,
    &:focus {
      color: $primary;
    }
  }

  .item-alert-tag {
    position: absolute;
    top: 0;
    left: 0;
    height: 26px;
    color: $white;
    font-weight: bold;
    background: $primary;
    padding: 0 12px;
    z-index: 2;
    display: flex;
    align-items: center;
  }

  .item-title-wrapper {
    display: flex;
    align-items: center;
    line-height: normal;
    font-weight: bold;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    margin-top: 8px;
    width: 95%;

    .item-title {
      flex-grow: 1;
      flex-shrink: 1;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    .item-csa {
      position: relative;
      vertical-align: unset;
      flex-shrink: 0;
      margin-top: -2px;
      margin-left: 2px;
    }
  }

  .item-season-sumup {
    flex-grow: 1;
    flex-shrink: 1;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

</style>
